import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import BaseListingRendererWithPlugins from 'nav/expanded/BaseListingRendererWithPlugins';
import {
  ColumnAccessorKey,
  ColumnConfiguration,
  ListingRendererType,
} from 'nav/expanded/ListingRendererConstants';
import * as ExpandedTableTagUtil from 'schemas/tags/ExpandedTableTagUtil';
import {SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import ConnectedAuthors from 'ui-library/ConnectedAuthors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ScrollableTableColumn} from 'ui-library/scrollable/ScrollableTableConstants';
import {DatetimeTableCell} from 'ui-library/scrollable/table-cells/DatetimeTableCell';
import {BasicEvent} from 'util/Types';

import {StudyListingGraphQL} from '../../../graphql-fragments/studies/__generated/StudyListingGraphQL';

export default class StudyListingRenderer extends BaseListingRendererWithPlugins<ListingRendererType.STUDY> {
  listingRendererType = ListingRendererType.STUDY;

  renderIcon(): JSX.Element {
    return <BenchlingIcon icon={ModelTypeConstants.STUDY} className="scrollableTable-cell-icon" />;
  }

  getNonPluginColumnConfigurations(
    studies: Array<StudyListingGraphQL>
  ): Array<ColumnConfiguration<StudyListingGraphQL>> {
    let columns: Array<ColumnConfiguration<StudyListingGraphQL>> = [
      {
        column: {
          accessorKey: ColumnAccessorKey.NAME,
          header: 'Name',
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_NAME_KEY,
        },
        render: (study: StudyListingGraphQL) => study.name,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.ID,
          header: getColumnHeaderFromSortKey(SortKey.SORT_IDENTIFIER_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_IDENTIFIER_KEY,
        },
        render: (study: StudyListingGraphQL) => study.humanId,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_CREATED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_CREATED_AT_KEY,
        },
        render: (study: StudyListingGraphQL) => (
          <DatetimeTableCell datetime={study.createdAt} showTimeInCell={false} />
        ),
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.MODIFIED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_MODIFIED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_MODIFIED_AT_KEY,
        },
        render: (study: StudyListingGraphQL) => (
          <DatetimeTableCell datetime={study.modifiedAt} showTimeInCell={false} />
        ),
      },
      {
        column: {accessorKey: ColumnAccessorKey.AUTHORS, header: 'Authors', flexGrow: 1},
        render: (study: StudyListingGraphQL) => <ConnectedAuthors itemId={study.id} />,
      },
      {
        column: {accessorKey: ColumnAccessorKey.DESCRIPTION, header: 'Description', flexGrow: 1},
        render: (study: StudyListingGraphQL) => study.description,
      },
      {
        column: {accessorKey: ColumnAccessorKey.PHASE, header: 'Phase', flexGrow: 1},
        render: (study: StudyListingGraphQL) => study.phase,
      },
    ];

    const tagColumns = ExpandedTableTagUtil.listSortedTagColumns(studies);
    if (tagColumns.length) {
      columns = columns.concat(
        tagColumns.map((column: ScrollableTableColumn) => ({
          column,
          render: (study: StudyListingGraphQL) => {
            const tagsByColumn = ExpandedTableTagUtil.mapTagsToColumns({
              tags: study.tags,
              tagColumns: [column],
            });
            return tagsByColumn.tagFields[column.accessorKey];
          },
        }))
      );
    }

    return columns;
  }
  getNonPluginRowContentHeight(study: StudyListingGraphQL): number {
    return ExpandedTableTagUtil.getRowHeight(study.tags);
  }

  openContextMenu(item: StudyListingGraphQL, e: BasicEvent, extras: unknown): void {
    // TODO(BNCH-77427): Implement
  }
}
